import { change, Field, getFormValues } from "redux-form";
import { useDispatch, useSelector } from "react-redux";
import { emailArray, requiredArray, emailValidator, required } from "forms/validators/validators";
import React, { useRef, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { FEED_CREATION_FORM } from "forms/list";
import { ReduxControls } from "rhombus-react";
import { noop } from "utils/common";

import "./FeedEmail.css";

function FeedEmail({ allowMultiple = true }) {
	const dispatch = useDispatch();
	const userEmail = useSelector(state => state.you.email);
	const formValues = useSelector(state => getFormValues(FEED_CREATION_FORM)(state));
	const currentEmails = allowMultiple ? (formValues?.feedEmails || []) : [];
	const currentSingleEmail = allowMultiple ? null : formValues?.feedEmail;

	const emailsRef = useRef(currentEmails);
	emailsRef.current = currentEmails;

	const notifyMe = useMemo(() => {
		if (!userEmail) {return false;}
		if (allowMultiple) {
			return currentEmails.some(email => email.value === userEmail);
		} else {
			return currentSingleEmail === userEmail;
		}
	}, [currentEmails, currentSingleEmail, userEmail, allowMultiple]);

	const handleNotifyMeChange = useCallback(() => {
		if (allowMultiple) {
			const latestEmails = emailsRef.current;
			const isCurrentlyChecked = latestEmails.some(email => email.value === userEmail);

			if (!isCurrentlyChecked && userEmail) {
				const updatedEmails = [...latestEmails, { value: userEmail, label: userEmail }];
				dispatch(change(FEED_CREATION_FORM, 'feedEmails', updatedEmails));
			} else if (isCurrentlyChecked && userEmail) {
				const updatedEmails = latestEmails.filter(email => email.value !== userEmail);
				dispatch(change(FEED_CREATION_FORM, 'feedEmails', updatedEmails));
			}
		} else {
			const isCurrentlyChecked = currentSingleEmail === userEmail;
			if (!isCurrentlyChecked && userEmail) {
				dispatch(change(FEED_CREATION_FORM, 'feedEmail', userEmail));
			} else if (isCurrentlyChecked) {
				dispatch(change(FEED_CREATION_FORM, 'feedEmail', ''));
			}
		}
	}, [allowMultiple, userEmail, dispatch, currentSingleEmail]);

	let emailField;
	if (allowMultiple) {
		emailField = (
			<Field
				className="form-email-input"
				name="feedEmails"
				label="Email"
				placeholder="user@company.com"
				component={ReduxControls.Select}
				allowCreate
				isMulti
				required
				isClearable={false}
				validate={[emailArray, requiredArray]}
				onMenuScrollToBottom={noop}
			/>
		);
	} else {
		emailField = (
			<Field
				className="form-email-input"
				name="feedEmail"
				label="Email"
				placeholder="user@company.com"
				component={ReduxControls.Input}
				type="email"
				required
				validate={[required, emailValidator]}
			/>
		);
	}

	return (
		<div className="feed-email-field">
			{emailField}

			<div className="notify-me-container">
				<ReduxControls.Checkbox
					label="Notify me too"
					className="notify-me-checkbox"
					checked={notifyMe}
					onChange={handleNotifyMeChange}
				/>
			</div>
		</div>
	);
}

FeedEmail.propTypes = {
	allowMultiple: PropTypes.bool,
};

export default FeedEmail;
